import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authApi } from '../api/auth';
import { adminCustomersApi, adminDiscountsApi, adminStaffApi } from '../api/operations';
import type { CreateStaffUserDto, CustomerGroupDto, CustomerQueryDto, DiscountType } from '../api/types';
import { Column, DataTable, SearchInput, SelectFilter, StatTile, Toolbar } from '../components/DataTable';
import { PageHeader } from '../components/Shell';
import {
  Badge,
  ConfirmDialog,
  ErrorState,
  Field,
  FormErrors,
  Modal,
  PageLoader,
  Pagination,
  Spinner,
} from '../components/ui';
import { useToast } from '../components/Toast';
import { useAuth } from '../context/AuthContext';
import { formatDate, formatDateTime, money, number, relativeTime } from '../lib/format';
import {
  financialStatusLabels,
  fulfillmentStatusLabels,
  labelOf,
} from '../lib/labels';
import { P, permissionLabels, roleLabels } from '../lib/permissions';

/* ================================ MUSTERILER ================================ */

const DEFAULT_CUSTOMER_GROUPS: CustomerGroupDto[] = [
  {
    id: 1,
    name: 'Perakende',
    slug: 'perakende',
    description: 'Varsayılan müşteri grubu.',
    discountPercentage: 0,
    showPricesWithoutTax: false,
    isTaxExempt: false,
    isDefault: true,
  },
  {
    id: 2,
    name: 'Toptan',
    slug: 'toptan',
    description: 'Toptan alım yapan kurumsal müşteriler.',
    discountPercentage: 0,
    showPricesWithoutTax: true,
    isTaxExempt: false,
    isDefault: false,
  },
  {
    id: 3,
    name: 'VIP',
    slug: 'vip',
    description: 'Sadık müşteriler için özel indirim grubu.',
    discountPercentage: 5,
    showPricesWithoutTax: false,
    isTaxExempt: false,
    isDefault: false,
  },
];

export function CustomersPage() {
  const navigate = useNavigate();
  const [query, setQuery] = useState<CustomerQueryDto>({ page: 1, pageSize: 20 });

  const customers = useQuery({
    queryKey: ['admin', 'customers', query],
    queryFn: () => adminCustomersApi.search(query),
    placeholderData: keepPreviousData,
  });

  const groupsQuery = useQuery({
    queryKey: ['admin', 'customer-groups'],
    queryFn: () => adminCustomersApi.groups(),
    retry: false,
  });
  const customerGroups = groupsQuery.data?.length ? groupsQuery.data : DEFAULT_CUSTOMER_GROUPS;

  const columns: Column<NonNullable<typeof customers.data>['items'][number]>[] = [
    {
      key: 'customer',
      header: 'Müşteri',
      cell: (row) => (
        <div className="min-w-0">
          <Link
            to={`/musteriler/${row.id}`}
            className="block truncate font-medium text-ink-900 hover:text-brand-700"
          >
            {row.fullName ?? row.email}
          </Link>
          <p className="truncate text-xs text-ink-400">{row.email}</p>
        </div>
      ),
    },
    {
      key: 'group',
      header: 'Grup',
      hideOnMobile: true,
      cell: (row) =>
        row.customerGroupName ? (
          <span
            className={`badge ${
              row.customerGroupName.toUpperCase() === 'VIP'
                ? 'bg-amber-50 text-amber-800'
                : row.customerGroupName.toLowerCase() === 'toptan'
                  ? 'bg-brand-50 text-brand-800'
                  : 'bg-ink-100 text-ink-700'
            }`}
          >
            {row.customerGroupName}
          </span>
        ) : (
          <span className="text-ink-300">—</span>
        ),
    },
    {
      key: 'orders',
      header: 'Sipariş',
      align: 'right',
      cell: (row) => number(row.totalOrderCount),
    },
    {
      key: 'spent',
      header: 'Harcama',
      align: 'right',
      cell: (row) => <span className="font-medium text-ink-900">{money(row.totalSpent)}</span>,
    },
    {
      key: 'lastOrder',
      header: 'Son sipariş',
      hideOnMobile: true,
      cell: (row) => <span className="text-ink-500">{formatDate(row.lastOrderAt)}</span>,
    },
    {
      key: 'status',
      header: 'Durum',
      cell: (row) => (
        <div className="flex flex-wrap gap-1">
          {row.isActive ? (
            <span className="badge bg-emerald-50 text-emerald-700">Aktif</span>
          ) : (
            <span className="badge bg-red-50 text-red-700">Askıda</span>
          )}
          {!row.emailConfirmed ? (
            <span className="badge bg-amber-50 text-amber-800">E-posta onaysız</span>
          ) : null}
          {row.lockoutEnd && new Date(row.lockoutEnd) > new Date() ? (
            <span className="badge bg-red-50 text-red-700">Kilitli</span>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Müşteriler"
        description={customers.data ? `${number(customers.data.totalCount)} kayıtlı müşteri` : undefined}
      />

      <Toolbar>
        <SearchInput
          value={query.search ?? ''}
          onChange={(value) => setQuery((c) => ({ ...c, search: value || undefined, page: 1 }))}
          placeholder="Ad, e-posta veya telefon"
        />

        <SelectFilter
          label="Müşteri grubu"
          value={query.customerGroupId === undefined || query.customerGroupId === null ? '' : String(query.customerGroupId)}
          onChange={(value) =>
            setQuery((c) => ({
              ...c,
              customerGroupId: value === '' ? undefined : Number(value),
              page: 1,
            }))
          }
          options={customerGroups.map((g) => ({
            value: String(g.id),
            label: g.name,
          }))}
        />

        <SelectFilter
          label="Hesap durumu"
          value={query.isActive === undefined ? '' : String(query.isActive)}
          onChange={(value) =>
            setQuery((c) => ({
              ...c,
              isActive: value === '' ? undefined : value === 'true',
              page: 1,
            }))
          }
          options={[
            { value: 'true', label: 'Aktif' },
            { value: 'false', label: 'Askıda' },
          ]}
        />

        <SelectFilter
          label="E-posta"
          value={query.emailConfirmed === undefined ? '' : String(query.emailConfirmed)}
          onChange={(value) =>
            setQuery((c) => ({
              ...c,
              emailConfirmed: value === '' ? undefined : value === 'true',
              page: 1,
            }))
          }
          options={[
            { value: 'true', label: 'Doğrulanmış' },
            { value: 'false', label: 'Doğrulanmamış' },
          ]}
        />

        <div>
          <label className="label" htmlFor="cst-minspent">
            En az harcama
          </label>
          <input
            id="cst-minspent"
            type="number"
            min={0}
            className="field w-32 py-2 text-sm"
            value={query.minSpent ?? ''}
            onChange={(event) =>
              setQuery((c) => ({
                ...c,
                minSpent: event.target.value ? Number(event.target.value) : undefined,
                page: 1,
              }))
            }
          />
        </div>

        <label className="mb-1 flex items-center gap-2 text-sm text-ink-700">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-ink-300 text-brand-600"
            checked={query.hasOrders === true}
            onChange={(event) =>
              setQuery((c) => ({ ...c, hasOrders: event.target.checked || undefined, page: 1 }))
            }
          />
          Sipariş vermiş olanlar
        </label>
      </Toolbar>

      <DataTable
        columns={columns}
        rows={customers.data?.items ?? []}
        rowKey={(row) => row.id}
        isLoading={customers.isLoading}
        error={customers.error}
        onRetry={() => void customers.refetch()}
        emptyMessage="Müşteri bulunamadı."
        onRowClick={(row) => navigate(`/musteriler/${row.id}`)}
        footer={
          customers.data ? (
            <Pagination
              page={customers.data.page}
              totalPages={customers.data.totalPages}
              totalCount={customers.data.totalCount}
              onChange={(page) => setQuery((c) => ({ ...c, page }))}
            />
          ) : null
        }
      />
    </div>
  );
}

export function CustomerDetailPage() {
  const { userId = '' } = useParams();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { can } = useAuth();

  const [editOpen, setEditOpen] = useState(false);
  const [group, setGroup] = useState<number | null>(null);
  const [adminNote, setAdminNote] = useState('');
  const [tags, setTags] = useState('');
  const [suspendOpen, setSuspendOpen] = useState(false);

  const customer = useQuery({
    queryKey: ['admin', 'customer', userId],
    queryFn: () => adminCustomersApi.getById(userId),
    enabled: userId.length > 0,
  });

  const groupsQuery = useQuery({
    queryKey: ['admin', 'customer-groups'],
    queryFn: () => adminCustomersApi.groups(),
    retry: false,
  });
  const customerGroups = groupsQuery.data?.length ? groupsQuery.data : DEFAULT_CUSTOMER_GROUPS;

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'customer', userId] });
    void queryClient.invalidateQueries({ queryKey: ['admin', 'customers'] });
  };

  const save = useMutation({
    mutationFn: () =>
      adminCustomersApi.update(userId, {
        customerGroupId: group,
        adminNote: adminNote || null,
        tags: tags || null,
      }),
    onSuccess: (message) => {
      toast.success(message ?? 'Müşteri güncellendi.');
      setEditOpen(false);
      invalidate();
    },
  });

  const setActive = useMutation({
    mutationFn: (isActive: boolean) =>
      adminCustomersApi.setActive(userId, {
        isActive,
        reason: isActive ? 'Yeniden etkinleştirildi' : 'Panelden askıya alındı',
      }),
    onSuccess: (message) => {
      toast.success(message ?? 'Hesap durumu güncellendi.');
      setSuspendOpen(false);
      invalidate();
    },
    onError: (error) => toast.fromError(error, 'İşlem tamamlanamadı.'),
  });

  const [couponOpen, setCouponOpen] = useState(false);
  const [couponForm, setCouponForm] = useState<{
    name: string;
    code: string;
    type: DiscountType;
    value: number;
    minimumSubtotal: number | null;
    endsAt: string;
    usageLimit: number;
  }>({
    name: 'Özel Müşteri İndirimi',
    code: '',
    type: 'FixedAmount',
    value: 100,
    minimumSubtotal: null,
    endsAt: '',
    usageLimit: 1,
  });

  const assignCoupon = useMutation({
    mutationFn: () =>
      adminDiscountsApi.quickAssign({
        userId,
        name: couponForm.name,
        code: couponForm.code || null,
        type: couponForm.type,
        value: couponForm.value,
        minimumSubtotal: couponForm.minimumSubtotal,
        endsAt: couponForm.endsAt ? new Date(couponForm.endsAt).toISOString() : null,
        usageLimit: couponForm.usageLimit || 1,
      }),
    onSuccess: (data) => {
      toast.success(data?.name ? `"${data.name}" kuponu başarıyla tanımlandı.` : 'Kupon tanımlandı.');
      setCouponOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'discounts'] });
    },
    onError: (err) => toast.fromError(err, 'Kupon tanımlanamadı.'),
  });

  if (customer.isLoading) return <PageLoader />;
  if (customer.isError)
    return <ErrorState error={customer.error} onRetry={() => void customer.refetch()} />;

  const data = customer.data!;

  return (
    <div>
      <PageHeader
        title={data.fullName ?? data.email}
        description={data.email}
        actions={
          <>
            <Link to="/musteriler" className="btn-secondary">
              Listeye dön
            </Link>
            {can(P.discounts.manage) ? (
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  setCouponForm({
                    name: `Özel İndirim (${data.fullName || data.email})`,
                    code: `OZEL-${Math.floor(1000 + Math.random() * 9000)}`,
                    type: 'FixedAmount',
                    value: 100,
                    minimumSubtotal: null,
                    endsAt: '',
                    usageLimit: 1,
                  });
                  setCouponOpen(true);
                }}
              >
                🎟️ Kupon Tanımla
              </button>
            ) : null}
            {can(P.customers.update) ? (
              <>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    setGroup(data.customerGroupId ?? null);
                    setAdminNote(data.adminNote ?? '');
                    setTags(data.tags ?? '');
                    setEditOpen(true);
                  }}
                >
                  Düzenle
                </button>
                <button
                  type="button"
                  className={data.isActive ? 'btn-danger' : 'btn-primary'}
                  onClick={() => setSuspendOpen(true)}
                >
                  {data.isActive ? 'Hesabı askıya al' : 'Hesabı aç'}
                </button>
              </>
            ) : null}
          </>
        }
      />

      <div className="mb-5 grid gap-4 sm:grid-cols-4">
        <StatTile label="Sipariş" value={number(data.totalOrderCount)} />
        <StatTile label="Toplam harcama" value={money(data.totalSpent)} />
        <StatTile label="Ortalama sepet" value={money(data.averageOrderValue)} />
        <StatTile
          label="Başarısız giriş"
          value={number(data.accessFailedCount)}
          tone={data.accessFailedCount > 0 ? 'negative' : 'neutral'}
          hint={
            data.lockoutEnd && new Date(data.lockoutEnd) > new Date()
              ? `Kilit bitişi: ${formatDateTime(data.lockoutEnd)}`
              : undefined
          }
        />
      </div>

      <div className="grid gap-5 lg:grid-cols-[1fr_320px]">
        <div className="space-y-5">
          <section className="card p-5">
            <h2 className="mb-4 text-sm font-semibold text-ink-900">Son siparişler</h2>
            {data.recentOrders.length === 0 ? (
              <p className="text-sm text-ink-500">Bu müşterinin siparişi yok.</p>
            ) : (
              <ul className="divide-y divide-ink-100">
                {data.recentOrders.map((order) => (
                  <li key={order.id} className="flex flex-wrap items-center gap-3 py-3">
                    <div className="min-w-0 flex-1">
                      <Link
                        to={`/siparisler/${order.id}`}
                        className="font-medium text-ink-900 hover:text-brand-700"
                      >
                        {order.orderNumber}
                      </Link>
                      <p className="text-xs text-ink-400">{formatDateTime(order.createdAt)}</p>
                    </div>
                    <Badge label={labelOf(financialStatusLabels, order.financialStatus)} />
                    <Badge label={labelOf(fulfillmentStatusLabels, order.fulfillmentStatus)} />
                    <span className="font-medium text-ink-900">
                      {money(order.grandTotal, order.currencyCode)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="card p-5">
            <h2 className="mb-4 text-sm font-semibold text-ink-900">Adresler</h2>
            {data.addresses.length === 0 ? (
              <p className="text-sm text-ink-500">Kayıtlı adres yok.</p>
            ) : (
              <ul className="grid gap-3 sm:grid-cols-2">
                {data.addresses.map((address) => (
                  <li key={address.id} className="rounded-lg border border-ink-200 p-3.5">
                    <p className="text-sm font-medium text-ink-900">
                      {address.title ?? `${address.firstName} ${address.lastName}`}
                    </p>
                    <p className="mt-1 text-sm leading-6 text-ink-600">
                      {address.addressLine1}
                      <br />
                      {address.district} / {address.city} · {address.phone}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {address.isDefaultShipping ? (
                        <span className="badge bg-brand-50 text-brand-800">Teslimat</span>
                      ) : null}
                      {address.isDefaultBilling ? (
                        <span className="badge bg-ink-100 text-ink-700">Fatura</span>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        <aside className="space-y-5">
          <section className="card p-5">
            <h2 className="mb-3 text-sm font-semibold text-ink-900">Hesap</h2>
            <dl className="space-y-2 text-sm">
              <Detail label="Kayıt" value={formatDate(data.createdAt)} />
              <Detail label="Son giriş" value={relativeTime(data.lastLoginAt)} />
              <Detail label="Son giriş IP" value={data.lastLoginIp ?? '—'} />
              <Detail label="Telefon" value={data.phoneNumber ?? '—'} />
              <Detail label="Doğum tarihi" value={formatDate(data.dateOfBirth)} />
              <Detail label="Grup" value={data.customerGroupName ?? '—'} />
              <Detail
                label="İki adımlı doğrulama"
                value={data.twoFactorEnabled ? 'Açık' : 'Kapalı'}
              />
              <Detail
                label="Pazarlama izni"
                value={data.acceptsMarketing ? 'Var' : 'Yok'}
              />
            </dl>
          </section>

          {data.companyName || data.taxNumber ? (
            <section className="card p-5">
              <h2 className="mb-3 text-sm font-semibold text-ink-900">Kurumsal bilgiler</h2>
              <dl className="space-y-2 text-sm">
                <Detail label="Firma" value={data.companyName ?? '—'} />
                <Detail label="Vergi dairesi" value={data.taxOffice ?? '—'} />
                <Detail label="Vergi no" value={data.taxNumber ?? '—'} />
              </dl>
            </section>
          ) : null}

          {data.adminNote || data.tags ? (
            <section className="card p-5">
              <h2 className="mb-3 text-sm font-semibold text-ink-900">Personel notu</h2>
              {data.adminNote ? <p className="text-sm text-ink-700">{data.adminNote}</p> : null}
              {data.tags ? (
                <div className="mt-2 flex flex-wrap gap-1">
                  {data.tags
                    .split(',')
                    .map((tag) => tag.trim())
                    .filter(Boolean)
                    .map((tag) => (
                      <span key={tag} className="badge bg-ink-100 text-ink-700">
                        {tag}
                      </span>
                    ))}
                </div>
              ) : null}
            </section>
          ) : null}
        </aside>
      </div>

      <Modal
        open={editOpen}
        title="Müşteriyi düzenle"
        onClose={() => setEditOpen(false)}
        footer={
          <>
            <button type="button" className="btn-secondary" onClick={() => setEditOpen(false)}>
              Vazgeç
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={() => save.mutate()}
              disabled={save.isPending}
            >
              {save.isPending ? <Spinner className="h-4 w-4" /> : null}
              Kaydet
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <FormErrors error={save.error} />

          <Field
            label="Müşteri grubu"
            htmlFor="cst-group"
            hint="Seçilen gruba göre özel fiyat listeleri, KDV görünümü veya VIP indirimleri uygulanır."
          >
            <select
              id="cst-group"
              className="field"
              value={group ?? ''}
              onChange={(event) => setGroup(event.target.value ? Number(event.target.value) : null)}
            >
              <option value="">— Grup yok (Standart) —</option>
              {customerGroups.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                  {item.discountPercentage > 0 ? ` (%${item.discountPercentage} İndirim)` : ''}
                  {item.showPricesWithoutTax ? ' (KDV hariç fiyat)' : ''}
                  {item.isDefault ? ' (Varsayılan)' : ''}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Personel notu" htmlFor="cst-note">
            <textarea
              id="cst-note"
              className="field min-h-24"
              value={adminNote}
              onChange={(event) => setAdminNote(event.target.value)}
            />
          </Field>

          <Field label="Etiketler" htmlFor="cst-tags" hint="Virgülle ayırın.">
            <input
              id="cst-tags"
              className="field"
              value={tags}
              onChange={(event) => setTags(event.target.value)}
            />
          </Field>
        </div>
      </Modal>

      <ConfirmDialog
        open={suspendOpen}
        title={data.isActive ? 'Hesabı askıya al' : 'Hesabı yeniden aç'}
        message={
          data.isActive
            ? 'Müşteri giriş yapamaz ve sipariş veremez. Mevcut siparişleri etkilenmez.'
            : 'Müşteri yeniden giriş yapabilecek.'
        }
        confirmLabel={data.isActive ? 'Askıya al' : 'Aç'}
        danger={data.isActive}
        busy={setActive.isPending}
        onCancel={() => setSuspendOpen(false)}
        onConfirm={() => setActive.mutate(!data.isActive)}
      />

      <Modal
        open={couponOpen}
        title="Müşteriye Özel Kupon Tanımla"
        description={`${data.fullName ?? data.email} müşterisine özel tanımlanacak indirim kuponu.`}
        onClose={() => setCouponOpen(false)}
        footer={
          <>
            <button type="button" className="btn-secondary" onClick={() => setCouponOpen(false)}>
              Vazgeç
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={() => assignCoupon.mutate()}
              disabled={assignCoupon.isPending || !couponForm.name || couponForm.value <= 0}
            >
              {assignCoupon.isPending ? <Spinner className="h-4 w-4" /> : null}
              Kuponu Tanımla ve Ata
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <FormErrors error={assignCoupon.error} />

          <Field label="Kupon Kampanya Adı" htmlFor="cpn-name" required>
            <input
              id="cpn-name"
              className="field"
              value={couponForm.name}
              onChange={(e) => setCouponForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="Örn: Özür Kuponu, Sadakat Hediyesi"
            />
          </Field>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Kupon Kodu" htmlFor="cpn-code" hint="Boş bırakılırsa sistem otomatik üretir.">
              <input
                id="cpn-code"
                className="field font-mono uppercase"
                value={couponForm.code}
                onChange={(e) => setCouponForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
                placeholder="Örn: OZEL-100TL"
              />
            </Field>

            <Field label="İndirim Türü" htmlFor="cpn-type">
              <select
                id="cpn-type"
                className="field"
                value={couponForm.type}
                onChange={(e) => setCouponForm((f) => ({ ...f, type: e.target.value as DiscountType }))}
              >
                <option value="FixedAmount">Sabit Tutar (₺)</option>
                <option value="Percentage">Yüzde İndirimi (%)</option>
              </select>
            </Field>

            <Field
              label={couponForm.type === 'Percentage' ? 'İndirim Oranı (%)' : 'İndirim Tutarı (₺)'}
              htmlFor="cpn-value"
              required
            >
              <input
                id="cpn-value"
                type="number"
                min="0.01"
                step="0.01"
                className="field"
                value={couponForm.value}
                onChange={(e) => setCouponForm((f) => ({ ...f, value: Number(e.target.value) }))}
              />
            </Field>

            <Field
              label="Asgari Sepet Tutarı (₺)"
              htmlFor="cpn-minsub"
              hint="İsteğe bağlı. Boş bırakılırsa sepet alt limiti aranmaz."
            >
              <input
                id="cpn-minsub"
                type="number"
                min="0"
                step="0.01"
                className="field"
                value={couponForm.minimumSubtotal ?? ''}
                onChange={(e) =>
                  setCouponForm((f) => ({
                    ...f,
                    minimumSubtotal: e.target.value ? Number(e.target.value) : null,
                  }))
                }
                placeholder="Limitsiz (boş)"
              />
            </Field>

            <Field label="Kullanım Sınırı (Adet)" htmlFor="cpn-limit">
              <input
                id="cpn-limit"
                type="number"
                min="1"
                className="field"
                value={couponForm.usageLimit}
                onChange={(e) => setCouponForm((f) => ({ ...f, usageLimit: Number(e.target.value) || 1 }))}
              />
            </Field>

            <Field label="Son Geçerlilik Tarihi" htmlFor="cpn-ends" hint="Boş bırakılırsa süresizdir.">
              <input
                id="cpn-ends"
                type="date"
                className="field"
                value={couponForm.endsAt}
                onChange={(e) => setCouponForm((f) => ({ ...f, endsAt: e.target.value }))}
              />
            </Field>
          </div>
        </div>
      </Modal>
    </div>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-ink-500">{label}</dt>
      <dd className="text-right text-ink-800">{value}</dd>
    </div>
  );
}

/* ================================= PERSONEL ================================= */

const emptyStaff: CreateStaffUserDto = {
  email: '',
  firstName: '',
  lastName: '',
  roles: [],
  sendInvitationEmail: true,
};

export function StaffPage() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { can, user } = useAuth();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState<CreateStaffUserDto>(emptyStaff);
  const [editingRoles, setEditingRoles] = useState<{ userId: string; roles: string[] } | null>(null);
  const [forceLogoutId, setForceLogoutId] = useState<string | null>(null);
  const [suspend, setSuspend] = useState<{ userId: string; isActive: boolean } | null>(null);

  const staff = useQuery({
    queryKey: ['admin', 'staff', page, search],
    queryFn: () => adminStaffApi.search({ page, pageSize: 20, search: search || undefined }),
    placeholderData: keepPreviousData,
  });

  const roles = useQuery({
    queryKey: ['admin', 'roles'],
    queryFn: () => adminStaffApi.roles(),
    staleTime: 10 * 60_000,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin', 'staff'] });

  const create = useMutation({
    mutationFn: () => adminStaffApi.create(form),
    onSuccess: () => {
      toast.success('Personel oluşturuldu.', 'Kurulum bağlantısı e-posta ile gönderildi.');
      setCreateOpen(false);
      setForm(emptyStaff);
      void invalidate();
    },
  });

  const updateRoles = useMutation({
    mutationFn: () =>
      adminStaffApi.updateRoles(editingRoles!.userId, { roles: editingRoles!.roles }),
    onSuccess: (message) => {
      toast.success(message ?? 'Roller güncellendi.', 'Kullanıcının oturumları yenilenecek.');
      setEditingRoles(null);
      void invalidate();
    },
  });

  const setActive = useMutation({
    mutationFn: () =>
      adminStaffApi.setActive(suspend!.userId, {
        isActive: suspend!.isActive,
        reason: 'Panelden değiştirildi',
      }),
    onSuccess: (message) => {
      toast.success(message ?? 'Durum güncellendi.');
      setSuspend(null);
      void invalidate();
    },
    onError: (error) => toast.fromError(error, 'İşlem tamamlanamadı.'),
  });

  const forceLogout = useMutation({
    mutationFn: (userId: string) => adminStaffApi.forceLogout(userId),
    onSuccess: (message) => {
      toast.success(message ?? 'Tüm oturumlar kapatıldı.');
      setForceLogoutId(null);
      void invalidate();
    },
    onError: (error) => toast.fromError(error, 'İşlem tamamlanamadı.'),
  });

  const columns: Column<NonNullable<typeof staff.data>['items'][number]>[] = [
    {
      key: 'user',
      header: 'Kullanıcı',
      cell: (row) => (
        <div className="min-w-0">
          <p className="truncate font-medium text-ink-900">{row.fullName ?? row.email}</p>
          <p className="truncate text-xs text-ink-400">{row.email}</p>
        </div>
      ),
    },
    {
      key: 'roles',
      header: 'Roller',
      cell: (row) => (
        <div className="flex flex-wrap gap-1">
          {row.roles.map((role) => (
            <span key={role} className="badge bg-ink-100 text-ink-700">
              {roleLabels[role] ?? role}
            </span>
          ))}
        </div>
      ),
    },
    {
      key: 'sessions',
      header: 'Açık oturum',
      align: 'right',
      hideOnMobile: true,
      cell: (row) => number(row.activeSessionCount),
    },
    {
      key: 'lastLogin',
      header: 'Son giriş',
      hideOnMobile: true,
      cell: (row) => <span className="text-ink-500">{relativeTime(row.lastLoginAt)}</span>,
    },
    {
      key: 'status',
      header: 'Durum',
      cell: (row) => (
        <div className="flex flex-wrap gap-1">
          {row.isActive ? (
            <span className="badge bg-emerald-50 text-emerald-700">Aktif</span>
          ) : (
            <span className="badge bg-red-50 text-red-700">Pasif</span>
          )}
          {row.twoFactorEnabled ? (
            <span className="badge bg-brand-50 text-brand-800">2FA</span>
          ) : null}
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      cell: (row) =>
        can(P.security.usersManage) && row.id !== user?.id ? (
          <div className="flex justify-end gap-1">
            <button
              type="button"
              className="btn-ghost btn-sm"
              onClick={() => setEditingRoles({ userId: row.id, roles: row.roles })}
            >
              Roller
            </button>
            <button
              type="button"
              className="btn-ghost btn-sm"
              onClick={() => setForceLogoutId(row.id)}
              title="Kullanıcının tüm oturumlarını anında kapatır"
            >
              Oturumları kapat
            </button>
            <button
              type="button"
              className={`btn-ghost btn-sm ${row.isActive ? 'text-red-600' : ''}`}
              onClick={() => setSuspend({ userId: row.id, isActive: !row.isActive })}
            >
              {row.isActive ? 'Pasife al' : 'Aktifleştir'}
            </button>
          </div>
        ) : null,
    },
  ];

  return (
    <div>
      <PageHeader
        title="Personel"
        description="Panel kullanıcıları ve rolleri"
        actions={
          can(P.security.usersManage) ? (
            <button type="button" className="btn-primary" onClick={() => setCreateOpen(true)}>
              Personel ekle
            </button>
          ) : null
        }
      />

      <Toolbar>
        <SearchInput
          value={search}
          onChange={(value) => {
            setSearch(value);
            setPage(1);
          }}
          placeholder="Ad veya e-posta"
        />
      </Toolbar>

      <DataTable
        columns={columns}
        rows={staff.data?.items ?? []}
        rowKey={(row) => row.id}
        isLoading={staff.isLoading}
        error={staff.error}
        onRetry={() => void staff.refetch()}
        emptyMessage="Personel bulunamadı."
        footer={
          staff.data ? (
            <Pagination
              page={staff.data.page}
              totalPages={staff.data.totalPages}
              totalCount={staff.data.totalCount}
              onChange={setPage}
            />
          ) : null
        }
      />

      {/* Roller ve izinler */}
      <section className="card mt-6 p-5">
        <h2 className="mb-4 text-sm font-semibold text-ink-900">Roller ve izinler</h2>
        {roles.isLoading ? (
          <p className="text-sm text-ink-500">Yükleniyor…</p>
        ) : (
          <ul className="grid gap-4 md:grid-cols-2">
            {(roles.data ?? []).map((role) => (
              <li key={role.id} className="rounded-lg border border-ink-200 p-4">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="text-sm font-medium text-ink-900">
                    {roleLabels[role.name] ?? role.name}
                  </h3>
                  <span className="text-xs text-ink-500">{role.userCount} kullanıcı</span>
                </div>
                {role.description ? (
                  <p className="mt-1 text-xs text-ink-500">{role.description}</p>
                ) : null}
                <div className="mt-2 flex flex-wrap gap-1">
                  {role.permissions.slice(0, 8).map((permission) => (
                    <span key={permission} className="badge bg-ink-100 text-[10px] text-ink-600">
                      {permissionLabels[permission] ?? permission}
                    </span>
                  ))}
                  {role.permissions.length > 8 ? (
                    <span className="badge bg-ink-100 text-[10px] text-ink-600">
                      +{role.permissions.length - 8}
                    </span>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <Modal
        open={createOpen}
        title="Personel ekle"
        description="Parola belirlenmez; kullanıcıya kurulum bağlantısı e-postayla gönderilir."
        onClose={() => setCreateOpen(false)}
        footer={
          <>
            <button type="button" className="btn-secondary" onClick={() => setCreateOpen(false)}>
              Vazgeç
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={() => create.mutate()}
              disabled={create.isPending || !form.email || form.roles.length === 0}
            >
              {create.isPending ? <Spinner className="h-4 w-4" /> : null}
              Oluştur
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <FormErrors error={create.error} />

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Ad" htmlFor="st-first" required>
              <input
                id="st-first"
                className="field"
                value={form.firstName}
                onChange={(event) => setForm((c) => ({ ...c, firstName: event.target.value }))}
              />
            </Field>
            <Field label="Soyad" htmlFor="st-last" required>
              <input
                id="st-last"
                className="field"
                value={form.lastName}
                onChange={(event) => setForm((c) => ({ ...c, lastName: event.target.value }))}
              />
            </Field>
          </div>

          <Field label="E-posta" htmlFor="st-email" required>
            <input
              id="st-email"
              type="email"
              className="field"
              value={form.email}
              onChange={(event) => setForm((c) => ({ ...c, email: event.target.value }))}
            />
          </Field>

          <Field label="Telefon" htmlFor="st-phone">
            <input
              id="st-phone"
              type="tel"
              className="field"
              value={form.phoneNumber ?? ''}
              onChange={(event) => setForm((c) => ({ ...c, phoneNumber: event.target.value }))}
            />
          </Field>

          <fieldset>
            <legend className="label">Roller</legend>
            <div className="space-y-1.5">
              {(roles.data ?? [])
                .filter((role) => role.name !== 'Customer')
                .map((role) => (
                  <label key={role.id} className="flex items-center gap-2 text-sm text-ink-700">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-ink-300 text-brand-600"
                      checked={form.roles.includes(role.name)}
                      onChange={(event) =>
                        setForm((c) => ({
                          ...c,
                          roles: event.target.checked
                            ? [...c.roles, role.name]
                            : c.roles.filter((item) => item !== role.name),
                        }))
                      }
                    />
                    {roleLabels[role.name] ?? role.name}
                  </label>
                ))}
            </div>
          </fieldset>
        </div>
      </Modal>

      <Modal
        open={editingRoles !== null}
        title="Rolleri güncelle"
        description="Rol değişikliği güvenlik damgasını yeniler; kullanıcının açık oturumları düşer."
        onClose={() => setEditingRoles(null)}
        footer={
          <>
            <button type="button" className="btn-secondary" onClick={() => setEditingRoles(null)}>
              Vazgeç
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={() => updateRoles.mutate()}
              disabled={updateRoles.isPending}
            >
              {updateRoles.isPending ? <Spinner className="h-4 w-4" /> : null}
              Kaydet
            </button>
          </>
        }
      >
        <div className="space-y-3">
          <FormErrors error={updateRoles.error} />

          {(roles.data ?? [])
            .filter((role) => role.name !== 'Customer')
            .map((role) => (
              <label key={role.id} className="flex items-center gap-2 text-sm text-ink-700">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-ink-300 text-brand-600"
                  checked={editingRoles?.roles.includes(role.name) ?? false}
                  onChange={(event) =>
                    setEditingRoles(
                      (current) =>
                        current && {
                          ...current,
                          roles: event.target.checked
                            ? [...current.roles, role.name]
                            : current.roles.filter((item) => item !== role.name),
                        },
                    )
                  }
                />
                {roleLabels[role.name] ?? role.name}
              </label>
            ))}
        </div>
      </Modal>

      <ConfirmDialog
        open={forceLogoutId !== null}
        title="Oturumları kapat"
        message="Kullanıcının tüm cihazlardaki oturumları anında sonlandırılır."
        confirmLabel="Kapat"
        danger
        busy={forceLogout.isPending}
        onCancel={() => setForceLogoutId(null)}
        onConfirm={() => forceLogoutId && forceLogout.mutate(forceLogoutId)}
      />

      <ConfirmDialog
        open={suspend !== null}
        title={suspend?.isActive ? 'Personeli aktifleştir' : 'Personeli pasife al'}
        message={
          suspend?.isActive
            ? 'Kullanıcı yeniden panele girebilecek.'
            : 'Kullanıcı panele giremez ve oturumları kapanır.'
        }
        confirmLabel={suspend?.isActive ? 'Aktifleştir' : 'Pasife al'}
        danger={!suspend?.isActive}
        busy={setActive.isPending}
        onCancel={() => setSuspend(null)}
        onConfirm={() => setActive.mutate()}
      />
    </div>
  );
}

/* =========================== HESABIM VE GUVENLIK =========================== */

export function AccountSecurityPage() {
  const { user, refresh, logout } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [profileForm, setProfileForm] = useState({
    firstName: '',
    lastName: '',
    phoneNumber: '',
  });

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: '',
    signOutOtherSessions: true,
  });

  const [emailForm, setEmailForm] = useState({ newEmail: '', currentPassword: '' });
  const [twoFactorPassword, setTwoFactorPassword] = useState('');

  useEffect(() => {
    if (user) {
      setProfileForm({
        firstName: user.firstName ?? '',
        lastName: user.lastName ?? '',
        phoneNumber: user.phoneNumber ?? '',
      });
    }
  }, [user]);

  const sessions = useQuery({
    queryKey: ['auth', 'sessions'],
    queryFn: () => authApi.sessions(),
  });

  const saveProfile = useMutation({
    mutationFn: () =>
      authApi.updateProfile({
        firstName: profileForm.firstName,
        lastName: profileForm.lastName,
        phoneNumber: profileForm.phoneNumber || null,
        dateOfBirth: user?.dateOfBirth ? user.dateOfBirth.slice(0, 10) : null,
        acceptsMarketing: user?.acceptsMarketing ?? false,
      }),
    onSuccess: async (message) => {
      toast.success(message ?? 'Profil bilgileriniz güncellendi.');
      await refresh();
    },
    onError: (error) => toast.fromError(error, 'Profil güncellenemedi.'),
  });

  const changePassword = useMutation({
    mutationFn: () => authApi.changePassword(passwordForm),
    onSuccess: (message) => {
      toast.success(message ?? 'Parolanız değiştirildi.');
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmNewPassword: '',
        signOutOtherSessions: true,
      });
      void queryClient.invalidateQueries({ queryKey: ['auth', 'sessions'] });
    },
  });

  const changeEmail = useMutation({
    mutationFn: () => authApi.requestEmailChange(emailForm),
    onSuccess: (message) => {
      toast.success(message ?? 'Doğrulama e-postası gönderildi.');
      setEmailForm({ newEmail: '', currentPassword: '' });
    },
  });

  const toggleTwoFactor = useMutation({
    mutationFn: () =>
      user?.twoFactorEnabled
        ? authApi.disableTwoFactor({ currentPassword: twoFactorPassword })
        : authApi.enableTwoFactor({ currentPassword: twoFactorPassword }),
    onSuccess: async (message) => {
      toast.success(message ?? 'İki adımlı doğrulama ayarı güncellendi.');
      setTwoFactorPassword('');
      await refresh();
    },
  });

  const revokeSession = useMutation({
    mutationFn: (sessionId: number) => authApi.revokeSession(sessionId),
    onSuccess: () => {
      toast.success('Oturum sonlandırıldı.');
      void queryClient.invalidateQueries({ queryKey: ['auth', 'sessions'] });
    },
    onError: (error) => toast.fromError(error, 'Oturum sonlandırılamadı.'),
  });

  const logoutAll = useMutation({
    mutationFn: () => authApi.logoutAll(),
    onSuccess: () => {
      toast.success('Tüm oturumlar kapatıldı.');
      void logout();
    },
  });

  return (
    <div className="space-y-6 max-w-4xl">
      <PageHeader
        title="Hesabım ve Güvenlik"
        description="Profil bilgilerinizi, giriş parolanızı, iki adımlı doğrulamayı ve açık oturumlarınızı yönetin"
      />

      {/* Profil Bilgileri */}
      <section className="card p-5">
        <h2 className="mb-4 text-sm font-semibold text-ink-900">Profil bilgileri</h2>
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            saveProfile.mutate();
          }}
        >
          <FormErrors error={saveProfile.error} />
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Ad" htmlFor="adm-prof-first" required>
              <input
                id="adm-prof-first"
                className="field"
                required
                value={profileForm.firstName}
                onChange={(event) =>
                  setProfileForm((c) => ({ ...c, firstName: event.target.value }))
                }
              />
            </Field>
            <Field label="Soyad" htmlFor="adm-prof-last" required>
              <input
                id="adm-prof-last"
                className="field"
                required
                value={profileForm.lastName}
                onChange={(event) =>
                  setProfileForm((c) => ({ ...c, lastName: event.target.value }))
                }
              />
            </Field>
            <Field label="Telefon" htmlFor="adm-prof-phone">
              <input
                id="adm-prof-phone"
                type="tel"
                className="field"
                value={profileForm.phoneNumber}
                onChange={(event) =>
                  setProfileForm((c) => ({ ...c, phoneNumber: event.target.value }))
                }
              />
            </Field>
          </div>
          <button type="submit" className="btn-primary" disabled={saveProfile.isPending}>
            {saveProfile.isPending ? <Spinner className="h-4 w-4" /> : null}
            Bilgileri kaydet
          </button>
        </form>
      </section>

      {/* Parola Degistir */}
      <section className="card p-5">
        <h2 className="mb-4 text-sm font-semibold text-ink-900">Parola değiştir</h2>
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            changePassword.mutate();
          }}
        >
          <FormErrors error={changePassword.error} />

          <Field label="Mevcut parola" htmlFor="adm-cur-pass" required>
            <input
              id="adm-cur-pass"
              type="password"
              autoComplete="current-password"
              required
              className="field"
              value={passwordForm.currentPassword}
              onChange={(event) =>
                setPasswordForm((c) => ({ ...c, currentPassword: event.target.value }))
              }
            />
          </Field>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="Yeni parola"
              htmlFor="adm-new-pass"
              required
              hint="Son 5 parolanız tekrar kullanılamaz."
            >
              <input
                id="adm-new-pass"
                type="password"
                autoComplete="new-password"
                required
                className="field"
                value={passwordForm.newPassword}
                onChange={(event) =>
                  setPasswordForm((c) => ({ ...c, newPassword: event.target.value }))
                }
              />
            </Field>
            <Field label="Yeni parola tekrar" htmlFor="adm-new-pass2" required>
              <input
                id="adm-new-pass2"
                type="password"
                autoComplete="new-password"
                required
                className="field"
                value={passwordForm.confirmNewPassword}
                onChange={(event) =>
                  setPasswordForm((c) => ({ ...c, confirmNewPassword: event.target.value }))
                }
              />
            </Field>
          </div>

          <label className="flex items-center gap-2 text-sm text-ink-700">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-ink-300 text-brand-600"
              checked={passwordForm.signOutOtherSessions}
              onChange={(event) =>
                setPasswordForm((c) => ({ ...c, signOutOtherSessions: event.target.checked }))
              }
            />
            Diğer cihazlardaki oturumları da kapat
          </label>

          <button type="submit" className="btn-primary" disabled={changePassword.isPending}>
            {changePassword.isPending ? <Spinner className="h-4 w-4" /> : null}
            Parolayı değiştir
          </button>
        </form>
      </section>

      {/* Iki Adimli Dogrulama */}
      <section className="card p-5">
        <h2 className="mb-2 text-sm font-semibold text-ink-900">İki adımlı doğrulama</h2>
        <p className="mb-4 text-sm text-ink-600">
          {user?.twoFactorEnabled
            ? 'Açık. Her girişte e-posta adresinize tek kullanımlık kod gönderilir.'
            : 'Kapalı. Açtığınızda her girişte e-postanıza kod gönderilir.'}
        </p>

        <form
          className="flex flex-wrap items-end gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            toggleTwoFactor.mutate();
          }}
        >
          <Field label="Parolanız" htmlFor="adm-tfa-pass" className="min-w-56 flex-1">
            <input
              id="adm-tfa-pass"
              type="password"
              required
              className="field"
              value={twoFactorPassword}
              onChange={(event) => setTwoFactorPassword(event.target.value)}
            />
          </Field>
          <button
            type="submit"
            className={user?.twoFactorEnabled ? 'btn-secondary' : 'btn-primary'}
            disabled={toggleTwoFactor.isPending}
          >
            {toggleTwoFactor.isPending ? <Spinner className="h-4 w-4" /> : null}
            {user?.twoFactorEnabled ? 'Kapat' : 'Aç'}
          </button>
        </form>
        <FormErrors error={toggleTwoFactor.error} />
      </section>

      {/* E-posta Degisikligi */}
      <section className="card p-5">
        <h2 className="mb-2 text-sm font-semibold text-ink-900">E-posta adresi</h2>
        <p className="mb-4 text-sm text-ink-600">
          Şu anki adres: <strong>{user?.email}</strong>. Yeni adrese doğrulama bağlantısı gönderilir.
        </p>

        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            changeEmail.mutate();
          }}
        >
          <FormErrors error={changeEmail.error} />

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Yeni e-posta" htmlFor="adm-new-email" required>
              <input
                id="adm-new-email"
                type="email"
                required
                className="field"
                value={emailForm.newEmail}
                onChange={(event) => setEmailForm((c) => ({ ...c, newEmail: event.target.value }))}
              />
            </Field>
            <Field label="Parolanız" htmlFor="adm-email-pass" required>
              <input
                id="adm-email-pass"
                type="password"
                required
                className="field"
                value={emailForm.currentPassword}
                onChange={(event) =>
                  setEmailForm((c) => ({ ...c, currentPassword: event.target.value }))
                }
              />
            </Field>
          </div>

          <button type="submit" className="btn-secondary" disabled={changeEmail.isPending}>
            {changeEmail.isPending ? <Spinner className="h-4 w-4" /> : null}
            Doğrulama gönder
          </button>
        </form>
      </section>

      {/* Acik Oturumlar */}
      <section className="card p-5">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-semibold text-ink-900">Açık oturumlar</h2>
          <button
            type="button"
            className="btn-secondary btn-sm"
            onClick={() => logoutAll.mutate()}
            disabled={logoutAll.isPending}
          >
            Tüm oturumları kapat
          </button>
        </div>

        {sessions.isLoading ? (
          <p className="text-sm text-ink-500">Yükleniyor…</p>
        ) : sessions.data && sessions.data.length > 0 ? (
          <ul className="divide-y divide-ink-100">
            {sessions.data.map((session) => (
              <li key={session.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-ink-900">
                    {session.deviceName ?? 'Bilinmeyen cihaz'}
                    {session.isCurrent ? (
                      <span className="badge ml-2 bg-emerald-50 text-emerald-700">Bu cihaz</span>
                    ) : null}
                  </p>
                  <p className="text-xs text-ink-500">
                    {session.ipAddress ?? '—'}
                    {session.location ? ` · ${session.location}` : ''} · son kullanım{' '}
                    {relativeTime(session.lastUsedAt ?? session.createdAt)}
                  </p>
                </div>
                {!session.isCurrent ? (
                  <button
                    type="button"
                    className="btn-ghost btn-sm text-red-600"
                    onClick={() => revokeSession.mutate(session.id)}
                    disabled={revokeSession.isPending}
                  >
                    Sonlandır
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-ink-500">Açık oturum bulunamadı.</p>
        )}
      </section>
    </div>
  );
}
